import { ChangeEventHandler, FC } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import {
	Accordion,
	ActionIcon,
	Button,
	Checkbox,
	Drawer,
	Flex,
	Text,
	Image,
	Title,
} from '@mantine/core';
import { creatorActions } from '@/store/slices/creatorSlice';
import { AttributeType } from '@/api/AttributeApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { UUID } from '@/types/types';
import { useMutation } from '@tanstack/react-query';
import { mapAttributesByRelation } from '@/utils/utilities';
import { SingleAttribute } from '../singleAttribute/SingleAttribute';
import { IconChevronLeft } from '@tabler/icons-react';
import CreatorProductModuleApi from '@/api/CreatorProductModuleApi';
import Logo from '@/assets/images/logo.png';

export const CreatorProductModuleConfigurationDrawer: FC = () => {
	const dispatch = useTypedDispatch();

	const { options, attributesWithValues, project, preset } = useTypedSelector(
		(state) => state.creator
	);

	const modules = options.isPresetMode
		? preset.creatorProductModules
		: project.creatorProductModules;

	const selectedCreatorProductModule = options.selectedCreatorProductModule;

	const handleChebkboxChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		dispatch(
			creatorActions.updateOptions({
				applyValueToAll: e.currentTarget.checked,
			})
		);
	};

	const deleteCreatorProductModuleMutation = useMutation({
		mutationFn: CreatorProductModuleApi.delete,
		onSuccess: () => {
			dispatch(
				creatorActions.removeCreatorProductModule(selectedCreatorProductModule!)
			);
		},
	});

	const handleRemoveCreatorModule = () => {
		if (!selectedCreatorProductModule) return;
		deleteCreatorProductModuleMutation.mutate(selectedCreatorProductModule.id);
	};

	const handleSelectAttributeValue =
		(attributeId: UUID) => (attributeValue: AttributeValue) => () => {
			if (options.applyValueToAll) {
				const changes = [
					{
						attributeId,
						attributeValue,
					},
				];

				if (attributeValue.relatedValue) {
					const relatedValue = attributesWithValues
						.find(
							(attr) => attr.id === attributeValue.relatedValue?.attributeId
						)
						?.values.find(
							(val) => val.id === attributeValue.relatedValue?.attributeValueId
						);

					if (relatedValue)
						changes.push({
							attributeId: attributeValue.relatedValue.attributeId,
							attributeValue: relatedValue,
						});
				}

				dispatch(creatorActions.handleCollectionAttributeValuesChange(changes));
				return;
			}

			const attributeValues = [attributeValue];

			if (attributeValue.relatedValue) {
				const relatedValue = attributesWithValues
					.find((attr) => attr.id === attributeValue.relatedValue?.attributeId)
					?.values.find(
						(val) => val.id === attributeValue.relatedValue?.attributeValueId
					);
				if (relatedValue) attributeValues.push(relatedValue);
			}

			dispatch(
				creatorActions.handleCreatorProductModuleConfiguration({
					creatorProductModule: selectedCreatorProductModule!,
					attributeValues,
				})
			);
		};

	const isDeleteable =
		modules.length > 1 && selectedCreatorProductModule?.deleteable;

	const moduleAttributes = attributesWithValues.filter(
		(attr) => attr.type === AttributeType.MODULE
	);

	const onClose = () => {
		dispatch(
			creatorActions.updateOptions({ selectedCreatorProductModule: null })
		);
	};

	return (
		<Drawer
			opened={!!options.selectedCreatorProductModule}
			onClose={onClose}
			title={
				<Flex justify="space-between">
					<Image src={Logo} w={200} className="creator-logo" />

					<ActionIcon onClick={onClose} variant="transparent">
						<IconChevronLeft />
					</ActionIcon>
				</Flex>
			}
		>
			<Title order={4} mt="md" mb={4}>
				Edytuj moduł {selectedCreatorProductModule?.productModule.name}
			</Title>
			<Text c="dimmed" mb="lg">
				Albo odnaczyć wybrany moduł kliknij prawym przyciskiem myszy na obszarze
				interaktywnym.
			</Text>
			<Accordion mt="lg">
				{selectedCreatorProductModule
					? mapAttributesByRelation(
							moduleAttributes,
							selectedCreatorProductModule.selectedAttributeValues
						).map((attribute) => {
							const selectedAttributeValue =
								selectedCreatorProductModule.selectedAttributeValues.find(
									(attr) => attr.attribute.id === attribute.id
								);

							return (
								<SingleAttribute
									key={attribute.id}
									attribute={attribute}
									selectedAttributeValue={selectedAttributeValue}
									handleSelectAttributeValue={handleSelectAttributeValue(
										attribute.id
									)}
								/>
							);
						})
					: null}
			</Accordion>
			<Checkbox
				label="Zastosuj do wszystkich"
				mt="lg"
				checked={options.applyValueToAll}
				onChange={handleChebkboxChange}
			/>
			<Flex justify="flex-end" mt="xl">
				{isDeleteable && (
					<Button
						color="red"
						size="sm"
						onClick={handleRemoveCreatorModule}
						fullWidth
					>
						Usuń
					</Button>
				)}
			</Flex>
		</Drawer>
	);
};
